import type { LineItemSyncResponse, LineItemOverride, RepairshoprInvoice } from '@techgitter/contracts';

const API_BASE_URL = '/api';

export async function syncJobLineItems(
  jobGuid: string,
  signal?: AbortSignal,
): Promise<LineItemSyncResponse> {
  const response = await fetch(
    `${API_BASE_URL}/jobs/${encodeURIComponent(jobGuid)}/line-items/sync`,
    {
      method: 'GET',
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to sync line items: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

export async function patchLineItemOverrides(
  jobGuid: string,
  overrides: LineItemOverride[],
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/jobs/${encodeURIComponent(jobGuid)}/line-items`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ overrides }),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to save line item overrides: ${response.status} ${response.statusText}`);
  }
}

export async function createRsLineItem(
  jobGuid: string,
  item: {
    name: string;
    quantity: number;
    price_retail: number;
    description?: string;
    product_id?: number;
    /**
     * Which ticket-type-template line minted this charge. The orchestrator
     * stamps it onto the line's SM4RT metadata once RepairShopr assigns an id
     * — we can't, because that metadata is keyed by the id RS is about to
     * mint. It is what stops a second edit-save duplicating the line.
     */
    templateKey?: string;
  },
): Promise<{ messageId: string; status: 'accepted' }> {
  const response = await fetch(
    `${API_BASE_URL}/jobs/${encodeURIComponent(jobGuid)}/line-items`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to create line item: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

export async function updateRsLineItem(
  jobGuid: string,
  rsLineItemId: number,
  updates: { quantity?: number; price_retail?: number; description?: string },
  signal?: AbortSignal,
): Promise<{ messageId: string; status: 'accepted' }> {
  const response = await fetch(
    `${API_BASE_URL}/jobs/${encodeURIComponent(jobGuid)}/line-items/${rsLineItemId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to update line item: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

export async function deleteRsLineItem(
  jobGuid: string,
  rsLineItemId: number,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/jobs/${encodeURIComponent(jobGuid)}/line-items/${rsLineItemId}`,
    {
      method: 'DELETE',
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to delete line item: ${response.status} ${response.statusText}`);
  }
}

export async function createJobInvoice(
  jobGuid: string,
  rsCustomerId: number,
  signal?: AbortSignal,
): Promise<{ messageId: string; status: 'accepted' }> {
  const response = await fetch(
    `${API_BASE_URL}/jobs/${encodeURIComponent(jobGuid)}/invoice`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rsCustomerId }),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to create invoice: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

export async function fetchJobInvoices(
  jobGuid: string,
  signal?: AbortSignal,
): Promise<{ invoices: RepairshoprInvoice[] | null }> {
  const response = await fetch(
    `${API_BASE_URL}/jobs/${encodeURIComponent(jobGuid)}/invoice`,
    {
      method: 'GET',
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch invoices: ${response.status} ${response.statusText}`);
  }

  return response.json();
}
